import clsx from 'clsx';
import { memo, type SVGAttributes, useCallback } from 'react';

type CircleProps = {
  /** Hole identifier */
  name: string;
  /** stroke colour (default "transparent") */
  stroke?: string;
  /** Fill colour */
  fill: string;
  /** Circle radius */
  radius: number;
  /** Stroke width */
  strokeWidth: number;
  /** Title for accessibility */
  title?: string;
  /** Transformation matrix for the drill hole, used to position and rotate the hole */
  transform?: SVGAttributes<SVGGElement>['transform'];
  /** Additional CSS classes for the drill hole */
  className?: string;
  /** Arbitrary HTML5 data attributes to attach to the SVG element */
  dataAttrs?: Record<string, string | boolean | undefined>;
};

/**
 * Single circle rendered as an interactive SVG element to represent a drill hole.
 * Selection styling is handled imperatively via D3 (not through props).
 *
 * The `<g>` wrapper uses a ref callback to set initial classes and aria attributes
 * so that React does not own them — D3 can toggle `selected` and `aria-pressed`
 * without React overwriting them on re-render.
 *
 * @example
 * ```tsx
 * {holes.map((hole) => (
 *   <Circle
 *     key={hole.hole_name}
 *     name={hole.hole_name}
 *     fill={interpolateOklch(hole.depth, min, max, colour)}
 *     radius={baseRadius}
 *     strokeWidth={strokeWidth}
 *     title={`Drill hole ${hole.hole_name}`}
 *     transform={`translate(${hole.x}, ${hole.y})`}
 *   />
 * ))}
 * ```
 */
const Circle = memo(
  ({ name, stroke, fill, radius, strokeWidth, title, transform, className, dataAttrs }: CircleProps) => {
    /** Sets initial classes and aria attributes once — React won't overwrite them on re-render */
    const gRef = useCallback((node: SVGGElement | null) => {
      if (!node) return;
      if (!node.classList.contains('drill_hole')) {
        node.classList.add('drill_hole', 'group');
        node.setAttribute('aria-pressed', 'false');
      }
    }, []);

    // Build data attributes object, filtering out undefined values
    const dataAttrsFiltered = dataAttrs
      ? Object.fromEntries(
          Object.entries(dataAttrs)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [`data-${key}`, value === true ? 'true' : value === false ? 'false' : value]),
        )
      : {};

    return (
      // biome-ignore lint/a11y/useSemanticElements: <g> with role="button" for SVG accessibility
      <g
        ref={gRef}
        className="group focus:outline-0"
        data-hole-name={name}
        aria-label={`Drill hole ${name}`}
        transform={transform}
        role="button"
        tabIndex={0}
        {...dataAttrsFiltered}
      >
        {title && <title>{title}</title>}
        <circle
          className={clsx(
            'hole-circle cursor-pointer transition-transform duration-150 ease-in-out focus:outline-0',
            className,
          )}
          r={radius}
          fill={fill}
          stroke={stroke || 'transparent'}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  },
);
Circle.displayName = 'Circle';

export { Circle, type CircleProps };
